import styled from 'styled-components';
import CsvDownload from 'react-json-to-csv';

const colors = {
    surface: '#2d4059',
    surfaceAlt: '#26384f',
    border: 'rgba(255, 255, 255, 0.12)',
    text: '#f7f9fc',
    muted: '#afbdd0',
    red: '#e30710',
    green: '#27c24c',
    blue: '#19a9d5',
};

export const Page = styled.main`
    box-sizing: border-box;
    width: calc(100% - 280px);
    min-height: calc(100vh - 55px);
    padding: 28px;
    color: ${colors.text};

    @media (max-width: 900px) {
        width: 100%;
        padding: 18px;
    }
`;

export const Header = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 22px;

    @media (max-width: 760px) {
        flex-direction: column;
    }
`;

export const Title = styled.h1`
    margin: 0;
    font-size: 28px;
    line-height: 1.2;
    letter-spacing: -0.02em;
`;

export const Subtitle = styled.p`
    margin: 7px 0 0;
    color: ${colors.muted};
    font-size: 14px;
`;

export const HeaderActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
`;

export const Button = styled.button`
    min-height: 40px;
    padding: 0 15px;
    border: 1px solid ${props => props.$primary ? colors.red : colors.border};
    border-radius: 8px;
    background: ${props => props.$primary ? colors.red : colors.surface};
    color: ${colors.text};
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: transform 120ms ease, background-color 120ms ease, border-color 120ms ease;

    &:hover {
        transform: translateY(-1px);
        background: ${props => props.$primary ? '#c90008' : '#354b67'};
    }

    &:focus-visible {
        outline: 3px solid rgba(25, 169, 213, 0.4);
        outline-offset: 2px;
    }
`;

export const CsvButton = styled(CsvDownload)`
    min-height: 40px;
    padding: 0 15px;
    border: 1px solid ${colors.border};
    border-radius: 8px;
    background: ${colors.surface};
    color: ${colors.text};
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:hover { background: #354b67; }
`;

export const Panel = styled.section`
    background: ${colors.surface};
    border: 1px solid ${colors.border};
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(8, 15, 25, 0.2);
`;

export const FilterPanel = styled(Panel)`
    padding: 18px;
    margin-bottom: 18px;
`;

export const FilterHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
    margin: 0;
    font-size: 16px;
`;

export const SourceBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 10px;
    border: 1px solid ${props => props.$fallback ? 'rgba(232, 186, 37, 0.35)' : 'rgba(39, 194, 76, 0.35)'};
    border-radius: 999px;
    background: ${props => props.$fallback ? 'rgba(232, 186, 37, 0.12)' : 'rgba(39, 194, 76, 0.12)'};
    color: ${props => props.$fallback ? '#f5cf58' : '#73dd8c'};
    font-size: 12px;
    font-weight: 700;

    &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
    }
`;

export const FilterGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(130px, 0.7fr) minmax(210px, 1.2fr) minmax(210px, 1.2fr) minmax(190px, 1fr);
    gap: 14px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

export const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 7px;
    color: ${colors.muted};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
`;

const controlStyles = `
    box-sizing: border-box;
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    background: #f8fafc;
    color: #202b3a;
    font: inherit;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: ${colors.blue};
        box-shadow: 0 0 0 3px rgba(25, 169, 213, 0.18);
    }
`;

export const Input = styled.input`${controlStyles}`;
export const Select = styled.select`${controlStyles}`;

export const StatsGrid = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
`;

export const TotalCard = styled.section`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-height: 92px;
    margin-bottom: 12px;
    padding: 18px 22px;
    border: 1px solid rgba(227, 7, 16, 0.35);
    border-left: 5px solid ${colors.red};
    border-radius: 10px;
    background: linear-gradient(100deg, rgba(227, 7, 16, 0.14), ${colors.surface} 42%);
    box-shadow: 0 8px 20px rgba(8, 15, 25, 0.14);

    @media (max-width: 560px) {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
    }
`;

export const TotalValue = styled.strong`
    display: block;
    margin-top: 6px;
    font-size: 38px;
    line-height: 1;
`;

export const TotalPaid = styled.div`
    min-width: 120px;
    padding: 10px 14px;
    border-radius: 9px;
    background: rgba(39, 194, 76, 0.13);
    color: #73dd8c;
    text-align: center;

    strong {
        display: block;
        font-size: 22px;
    }

    span {
        display: block;
        margin-top: 3px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
    }
`;

export const StatCard = styled.article`
    min-height: 112px;
    padding: 16px;
    border: 1px solid ${props => props.$highlight ? 'rgba(25, 169, 213, 0.5)' : colors.border};
    border-top: 3px solid ${props => props.$accent || colors.blue};
    border-radius: 10px;
    background: ${props => props.$highlight
        ? 'linear-gradient(145deg, rgba(25, 169, 213, 0.2), #2d4059 70%)'
        : colors.surface};
    box-shadow: 0 8px 20px rgba(8, 15, 25, 0.14);
`;

export const StatLabel = styled.p`
    margin: 0;
    color: ${colors.muted};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const StatValue = styled.strong`
    display: block;
    margin-top: 9px;
    font-size: 30px;
    line-height: 1;
`;

export const StatMeta = styled.p`
    margin: 10px 0 0;
    color: ${props => props.$paid ? '#73dd8c' : colors.muted};
    font-size: 13px;
`;

export const TablePanel = styled(Panel)`
    min-width: 0;
    overflow: hidden;
`;

export const TableHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 18px;
    border-bottom: 1px solid ${colors.border};
`;

export const ResultCount = styled.span`
    color: ${colors.muted};
    font-size: 13px;
`;

export const TableScroll = styled.div`
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
`;

export const DataTable = styled.table`
    width: 100%;
    min-width: 1100px;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;

    th:nth-child(1) { width: 8%; }
    th:nth-child(2) { width: 15%; }
    th:nth-child(3) { width: 17%; }
    th:nth-child(4) { width: 9%; }
    th:nth-child(5) { width: 12%; }
    th:nth-child(6) { width: 13%; }
    th:nth-child(7) { width: 8%; }
    th:nth-child(8) { width: 10%; }
    th:nth-child(9) { width: 8%; }

    th {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 12px 14px;
        background: ${colors.surfaceAlt};
        color: ${colors.muted};
        text-align: left;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.045em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    td {
        padding: 14px;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        color: ${colors.text};
        vertical-align: middle;
        overflow-wrap: anywhere;
    }

    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(8),
    td:nth-child(8) {
        white-space: nowrap;
        overflow-wrap: normal;
    }

    tbody tr[data-payment-status='paid'] {
        background: rgba(39, 194, 76, 0.09);
    }

    tbody tr[data-payment-status='unpaid'] {
        background: rgba(227, 7, 16, 0.14);
    }

    tbody tr[data-payment-status='paid'] > td:first-child {
        box-shadow: inset 4px 0 0 ${colors.green};
    }

    tbody tr[data-payment-status='unpaid'] > td:first-child {
        box-shadow: inset 4px 0 0 ${colors.red};
    }

    tbody tr[data-payment-status='paid']:hover {
        background: rgba(39, 194, 76, 0.15);
    }

    tbody tr[data-payment-status='unpaid']:hover {
        background: rgba(227, 7, 16, 0.2);
    }

    a {
        color: #6fd2f1;
        font-weight: 700;
        text-decoration: none;
    }

    a:hover { text-decoration: underline; }
`;

export const PrimaryText = styled.span`
    display: block;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

export const SecondaryText = styled.span`
    display: block;
    max-width: 100%;
    margin-top: 3px;
    color: ${colors.muted};
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
`;

export const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 9px;
    border-radius: 999px;
    background: ${props => props.$tone === 'success' ? 'rgba(39, 194, 76, 0.14)' :
        props.$tone === 'warning' ? 'rgba(232, 186, 37, 0.14)' :
        props.$tone === 'danger' ? 'rgba(227, 7, 16, 0.14)' : 'rgba(175, 189, 208, 0.12)'};
    color: ${props => props.$tone === 'success' ? '#73dd8c' :
        props.$tone === 'warning' ? '#f5cf58' :
        props.$tone === 'danger' ? '#ff8187' : '#c6d2e2'};
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
`;

export const PackageBadge = styled(Badge)`
    background: ${props => props.$background || colors.blue};
    color: ${props => props.$foreground || '#17313a'};
`;

export const ActionGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
`;

export const IconButton = styled.button`
    width: 30px;
    height: 32px;
    flex: 0 0 30px;
    padding: 0;
    border: 1px solid ${colors.border};
    border-radius: 7px;
    background: ${props => props.$danger ? 'rgba(227, 7, 16, 0.12)' : 'rgba(255, 255, 255, 0.06)'};
    color: ${props => props.$danger ? '#ff8187' : colors.text};
    cursor: pointer;

    &:hover { background: ${props => props.$danger ? 'rgba(227, 7, 16, 0.24)' : 'rgba(255, 255, 255, 0.12)'}; }
    &:focus-visible { outline: 2px solid ${colors.blue}; outline-offset: 2px; }
`;

export const InlineEditor = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;

    textarea {
        box-sizing: border-box;
        width: 190px;
        min-height: 66px;
        padding: 8px;
        border: 1px solid ${colors.blue};
        border-radius: 7px;
        resize: vertical;
        font: inherit;
    }
`;

export const PaymentStatusEditor = styled.div`
    display: grid;
    justify-items: start;
    gap: 6px;
    min-width: 100px;

    label {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: ${props => props.$paid ? '#73dd8c' : '#ff8187'};
        font-size: 12px;
        font-weight: 800;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
    }

    input {
        appearance: none;
        display: inline-grid;
        place-content: center;
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        margin: 0;
        border: 2px solid ${props => props.$paid ? colors.green : colors.red};
        border-radius: 5px;
        background: ${props => props.$paid ? colors.green : 'rgba(227, 7, 16, 0.15)'};
        cursor: pointer;
    }

    input::after {
        content: '✓';
        color: #fff;
        font-size: 14px;
        font-weight: 900;
        line-height: 1;
        transform: scale(0);
    }

    input:checked::after { transform: scale(1); }
    input:focus-visible { outline: 3px solid rgba(25, 169, 213, 0.45); outline-offset: 2px; }
    input:disabled, button:disabled { cursor: wait; opacity: 0.7; }

    button {
        padding: 0;
        border: 0;
        background: transparent;
        color: ${colors.muted};
        font: inherit;
        font-size: 11px;
        font-weight: 700;
        text-decoration: underline;
        text-underline-offset: 2px;
        cursor: pointer;
    }

    button:hover { color: ${colors.text}; }
    button:focus-visible { outline: 2px solid ${colors.blue}; outline-offset: 3px; }
`;

export const SmallButton = styled.button`
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    background: ${props => props.$secondary ? 'rgba(255, 255, 255, 0.12)' : colors.green};
    color: ${colors.text};
    font: inherit;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
`;

export const EmptyState = styled.div`
    padding: 46px 20px;
    color: ${colors.muted};
    text-align: center;

    i { display: block; margin-bottom: 10px; font-size: 28px; }
`;
